import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import { getTemplates } from "../../redux/ducks/template";

const PickerBox = styled(Box)({
    width: "100%",
});

const StatusRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
}));

const PreviewBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const PreviewLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    marginBottom: theme.spacing(0.5),
}));

const PreviewLine = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
}));

const QuoteTemplatePicker = ({
    value,
    onChange,
    disabled = false,
    label = "Quote template",
    helperText = "Pick the standard engagement this quote should follow.",
}) => {
    const dispatch = useDispatch();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
        setLoading(true);
        setFailed(false);
        dispatch(getTemplates("quote", 0, 200, (data) => {
            setLoading(false);
            if (!data) {
                setFailed(true);
                return;
            }
            setTemplates(data.filter((t) => t.isActive !== false || t._id === valueRef.current));
        }));
    }, [dispatch]);

    const selected = templates.find((t) => t._id === value) || null;
    const totalHours = selected ? (selected.lineItems || []).reduce((sum, li) => sum + (Number(li.hours) || 0), 0) : 0;

    if (loading) {
        return (
            <StatusRow>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading quote templates…</Typography>
            </StatusRow>
        );
    }

    if (failed) {
        return (
            <Typography variant="body2" color="error">
                We couldn't load your quote templates. Close this and try again.
            </Typography>
        );
    }

    if (templates.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No active quote templates yet. Create one on the Templates page, or save an existing quote as a template.
            </Typography>
        );
    }

    return (
        <PickerBox>
            <FormControl fullWidth disabled={disabled}>
                <InputLabel shrink>{label}</InputLabel>
                <Select
                    value={value || ""}
                    label={label}
                    notched
                    displayEmpty
                    onChange={(e) => {
                        const id = e.target.value;
                        onChange(id, templates.find((t) => t._id === id) || null);
                    }}
                >
                    <MenuItem value=""><em>No template</em></MenuItem>
                    {templates.map((t) => (
                        <MenuItem key={t._id} value={t._id}>{t.isActive === false ? `${t.name} (retired)` : t.name}</MenuItem>
                    ))}
                </Select>
                <FormHelperText>{helperText}</FormHelperText>
            </FormControl>

            {selected && (
                <PreviewBox>
                    <PreviewLabel variant="body2">{selected.name}</PreviewLabel>
                    <Typography variant="body2">{selected.description || "This template has no description."}</Typography>
                    <PreviewLine variant="body2">
                        {(selected.lineItems || []).length} line item{(selected.lineItems || []).length === 1 ? "" : "s"} · {totalHours}h · {selected.billingType || "Fixed Price"}
                    </PreviewLine>
                </PreviewBox>
            )}
        </PickerBox>
    );
};

export default QuoteTemplatePicker;
